import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, mergeProps as _mergeProps, normalizeStyle as _normalizeStyle, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "text-center py-10 text-grey-darken-1"
}
const _hoisted_2 = { class: "d-flex justify-space-between align-center flex-wrap gap-2" }
const _hoisted_3 = { class: "bg-grey-lighten-5 px-5 py-2 text-caption font-weight-bold text-grey-darken-2 text-uppercase tracking-wide border-b" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = { class: "flex-1 min-w-0" }
const _hoisted_6 = { class: "font-weight-medium text-grey-darken-4" }
const _hoisted_7 = {
  key: 0,
  class: "text-caption text-grey mt-0.5"
}
const _hoisted_8 = { class: "flex items-center gap-4" }
const _hoisted_9 = { class: "flex justify-between px-5 py-2 bg-grey-lighten-5 font-weight-bold text-body-2 border-t" }
const _hoisted_10 = {
  key: 1,
  class: "bg-white border-2 border-grey-darken-4 rounded-lg pa-4 flex justify-between text-body-1 font-weight-bold mt-4",
  "data-cy": "lien-grand-total"
}
const _hoisted_11 = {
  key: 2,
  class: "d-flex justify-space-between align-center px-3 py-3 mt-4",
  style: {"border-top":"2px solid #e0e0e0"},
  "data-cy": "lien-grand-total"
}
const _hoisted_12 = { class: "sett-step-footer" }

import { ref, reactive, computed, onMounted } from 'vue';
import { useSettlementStore } from '@/modules/matters/settlement/stores/useSettlementStore';
import { useLienStore } from '@/stores/useLienStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { formatCurrency, pluralizeWithCount } from '@/services/utils';
import { LIEN_STATUS_OPTIONS, LIEN_STATUS_LABELS, STATUS_DOT_COLORS, STATUS_STYLES } from '@/constants/lienConstants';
import type { LienData } from '@/models/lienTypes';
import { countUnpushedFinalizedLiens, isPushed } from '@/utils/lienHelpers';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import SkeletonRenderer from '@/components/SkeletonRenderer.vue';
import LienFormPanel from '@/modules/matters/LienFormPanel.vue';
import type { FromTableLien } from '@/models/settlementTypes';

const pushedStatusTooltip =
  'This lien has been submitted to QuickBooks. Undo the submission before changing its status.';


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementLienStepFF',
  props: {
  settlementId: { type: String, required: true },
  matterId: { type: String, required: true },
  state: { type: Object, required: false, default: undefined },
  /** Gate the SC-45784 redesign UI (bar-style total, confirm-state emphasis) behind this flag.
   *  false = legacy design (boxed "TOTAL LIENS", static bold weight, unchanged).
   *  true  = new design (totals bar matching Step 1/2, italic/gray → bold/black on confirm). */
  redesignEnabled: { type: Boolean, default: false },
},
  emits: ["confirmed", "go-back"],
  setup(__props, { emit: __emit }) {

const props = __props;

const emit = __emit;

const settlementStore = useSettlementStore();
const lienStore = useLienStore();
const { handleError, handleSuccess } = useSnackbarHandler();

const statusOptions = LIEN_STATUS_OPTIONS;

const uiState = reactive({
  loading: true,
  saving: false,
  loadError: false,
});

// ── Edit panel state ─────────────────────────────────────
const showEditPanel = ref(false);
const editingLien = ref<LienData | null>(null);
const panelLoading = ref(false);

const liensData = computed(() => settlementStore.fromTableLiens);

const grandTotal = computed(() => liensData.value?.totalLiens ?? 0);

/** Switches lien amounts from italic/gray to bold/black once Step 4 is confirmed. */
const stepConfirmed = computed(() => settlementStore.completedSteps.includes(4));

/** Shared class for every lien amount/label under the redesign — single source of
 *  truth for the confirm-state emphasis rule (avoids repeating the ternary per row). */
const amountEmphasisClass = computed(() =>
  (stepConfirmed.value ? 'font-weight-bold' : 'font-italic text-grey-darken-1'));

// All liens flattened across groups — for QBO push checks.
const allLiens = computed(() =>
  liensData.value?.lienGroups?.flatMap((g) => g.liens) ?? [],
);

const unpushedFinalizedCount = computed(() =>
  countUnpushedFinalizedLiens(allLiens.value),
);

// Non-blocking warning — when connected to QBO and ≥1 finalized lien is unpushed.
const showUnpushedWarning = computed(
  () =>
    (liensData.value?.isQboConnected ?? false) &&
    unpushedFinalizedCount.value > 0,
);

const confirmDisabledReason = computed(() => {
  if (uiState.loadError) return 'Unable to load lien data. Please retry.';
  if (liensData.value?.hasUnfinalizedLiens) return 'All liens must be Finalized or submitted to QuickBooks before confirming.';
  return undefined;
});

// ── Lifecycle ────────────────────────────────────────────
onMounted(async () => {
  await loadLiensFromTable();
});

// ── Actions ──────────────────────────────────────────────
async function loadLiensFromTable() {
  uiState.loading = true;
  uiState.loadError = false;
  try {
    await settlementStore.loadLiensFromTable();
  } catch {
    uiState.loadError = true;
  } finally {
    uiState.loading = false;
  }
}

async function updateLienStatus(lien: FromTableLien, newStatus: string) {
  try {
    await lienStore.updateLienStatus(lien.lienId, newStatus);
    handleSuccess('Lien status updated');
    await loadLiensFromTable();
  } catch (e) {
    handleError(e);
  }
}

async function confirmLiens() {
  uiState.saving = true;
  try {
    await settlementStore.confirmLiensFF();
    emit('confirmed');
  } catch (e: any) {
    if (e.response?.data?.error === 'LIENS_NOT_FINALIZED') {
      handleError('All liens must be Finalized or submitted to QuickBooks before confirming.');
      await loadLiensFromTable();
    }
  } finally {
    uiState.saving = false;
  }
}

async function openEditPanel(lien: FromTableLien) {
  try {
    // If panel is already open, close it first so the Transition animates out/in
    if (showEditPanel.value) {
      showEditPanel.value = false;
      await new Promise((r) => setTimeout(r, 10));
    }
    // Open panel immediately with skeleton, then fetch full lien data
    editingLien.value = null;
    panelLoading.value = true;
    showEditPanel.value = true;
    editingLien.value = await lienStore.fetchLienById(lien.lienId);
  } catch (e) {
    showEditPanel.value = false;
    handleError(e);
  } finally {
    panelLoading.value = false;
  }
}

async function onLienSaved() {
  // Panel closes itself; just refresh data so totals/statuses update
  editingLien.value = null;
  await loadLiensFromTable();
}

function goToLiensTab() {
  props.state?.go('matters-next.client.matter.liens');
}


return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_card, {
      flat: "",
      class: "border-0 !bg-[rgb(var(--v-theme-surface))]"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card_title, { class: "d-flex justify-space-between align-center" }, {
          default: _withCtx(() => [
            _cache[3] || (_cache[3] = _createElementVNode("span", null, "Step 4: Review Third-Party Liens", -1)),
            _createElementVNode("a", {
              class: "text-primary text-body-2 font-weight-medium text-decoration-none",
              "data-cy": "edit-on-liens-tab",
              href: "#",
              onClick: _withModifiers(goToLiensTab, ["prevent"])
            }, _toDisplayString(__props.redesignEnabled ? 'Manage liens on the Liens tab →' : 'Go to liens tab →'), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_v_card_text, null, {
          default: _withCtx(() => [
            (uiState.loading)
              ? (_openBlock(), _createBlock(SkeletonRenderer, {
                  key: 0,
                  spec: [
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
          { type: 'list-item' },
        ],
                  "container-class": "mb-4"
                }))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  (!liensData.value || !liensData.value.lienGroups?.some(g => g.liens.length > 0))
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[4] || (_cache[4] = [
                        _createElementVNode("div", { class: "font-weight-bold mb-1" }, "No liens recorded", -1),
                        _createElementVNode("div", { class: "text-body-2 text-grey-darken-1" }, " Add liens on the Liens tab to include them in the settlement statement. ", -1)
                      ]))]))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        (showUnpushedWarning.value)
                          ? (_openBlock(), _createBlock(AlertBar, {
                              key: 0,
                              type: "warning",
                              variant: "tonal",
                              class: "mb-4",
                              "data-cy": "unpushed-liens-warning"
                            }, {
                              default: _withCtx(() => [
                                _createElementVNode("div", _hoisted_2, [
                                  _createElementVNode("span", null, _toDisplayString(_unref(pluralizeWithCount)(unpushedFinalizedCount.value, 'finalized lien')) + " not yet submitted to QuickBooks. Submit from the Liens tab before finalizing the settlement to ensure trust checks are created in QuickBooks. ", 1),
                                  _createVNode(LlSecondaryButton, {
                                    variant: "outlined",
                                    color: "warning",
                                    size: "small",
                                    "data-cy": "go-to-liens-tab-warning",
                                    onClick: goToLiensTab
                                  }, {
                                    default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                      _createTextVNode(" Go to Liens Tab → ", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ])
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true),
                        _createVNode(AlertBar, {
                          type: "info",
                          class: "mb-4"
                        }, {
                          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                            _createElementVNode("span", { class: "font-weight-bold" }, "Liens are loaded from the Liens tab.", -1),
                            _createTextVNode(" All liens are included in the settlement statement. ", -1)
                          ]))]),
                          _: 1
                        }),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(liensData.value.lienGroups, (group) => {
                          return _withDirectives((_openBlock(), _createElementBlock("div", {
                            key: group.lienType ?? 'unknown',
                            class: "bg-white border rounded-lg mb-3 overflow-hidden"
                          }, [
                            _createElementVNode("div", _hoisted_3, _toDisplayString(group.displayName) + " Liens ", 1),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.liens, (lien, idx) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: lien.lienId,
                                class: _normalizeClass(["flex justify-between items-center px-5 py-2 text-body-2 cursor-pointer hover:bg-grey-lighten-5 transition-colors", idx < group.liens.length - 1 ? 'border-b border-grey-lighten-4' : '']),
                                "data-cy": "lien-row",
                                onClick: ($event: any) => (openEditPanel(lien))
                              }, [
                                _createElementVNode("div", _hoisted_5, [
                                  _createElementVNode("div", _hoisted_6, _toDisplayString(lien.payeeName), 1),
                                  (lien.description)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(lien.description), 1))
                                    : _createCommentVNode("", true)
                                ]),
                                _createElementVNode("div", _hoisted_8, [
                                  _createVNode(LlTooltip, {
                                    text: _unref(isPushed)(lien.status) ? pushedStatusTooltip : undefined
                                  }, {
                                    activator: _withCtx(() => [
                                      _createElementVNode("div", {
                                        onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                                      }, [
                                        _createVNode(_component_v_menu, {
                                          disabled: _unref(isPushed)(lien.status)
                                        }, {
                                          activator: _withCtx(({ props: statusMenuProps }) => [
                                            _createVNode(_component_v_chip, _mergeProps({ ref_for: true }, statusMenuProps, {
                                              size: "small",
                                              rounded: "pill",
                                              disabled: _unref(isPushed)(lien.status),
                                              class: ["cursor-pointer lien-status-badge font-weight-medium", `lien-status--${lien.status.toLowerCase()}`],
                                              "data-cy": "lien-status-chip"
                                            }), {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(_unref(LIEN_STATUS_LABELS)[lien.status.toLowerCase()] ?? lien.status) + " ", 1),
                                                _createVNode(_component_v_icon, {
                                                  size: "12",
                                                  class: "lien-status-caret"
                                                }, {
                                                  default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                                    _createTextVNode("mdi-chevron-down", -1)
                                                  ]))]),
                                                  _: 1
                                                })
                                              ]),
                                              _: 2
                                            }, 1040, ["disabled", "class"])
                                          ]),
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_list, {
                                              density: "compact",
                                              class: "!rounded-lg !shadow-xl min-w-[160px] !py-1"
                                            }, {
                                              default: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(statusOptions), (opt) => {
                                                  return (_openBlock(), _createBlock(_component_v_list_item, {
                                                    key: opt.value,
                                                    class: _normalizeClass(['transition-colors', lien.status === opt.value ? 'font-semibold' : 'text-gray-700']),
                                                    style: _normalizeStyle(lien.status === opt.value ? _unref(STATUS_STYLES)[opt.value] : {}),
                                                    onClick: ($event: any) => (updateLienStatus(lien, opt.value))
                                                  }, {
                                                    prepend: _withCtx(() => [
                                                      _createElementVNode("span", {
                                                        class: "w-2 h-2 rounded-full flex-shrink-0 mr-2",
                                                        style: _normalizeStyle({ backgroundColor: _unref(STATUS_DOT_COLORS)[opt.value] ?? '#94a3b8' })
                                                      }, null, 4)
                                                    ]),
                                                    default: _withCtx(() => [
                                                      _createVNode(_component_v_list_item_title, { class: "text-sm" }, {
                                                        default: _withCtx(() => [
                                                          _createTextVNode(_toDisplayString(opt.label), 1)
                                                        ]),
                                                        _: 2
                                                      }, 1024)
                                                    ]),
                                                    _: 2
                                                  }, 1032, ["class", "style", "onClick"]))
                                                }), 128))
                                              ]),
                                              _: 2
                                            }, 1024)
                                          ]),
                                          _: 2
                                        }, 1032, ["disabled"])
                                      ])
                                    ]),
                                    _: 2
                                  }, 1032, ["text"]),
                                  _createElementVNode("span", {
                                    class: _normalizeClass(["text-right min-w-[96px]", __props.redesignEnabled ? amountEmphasisClass.value : 'font-weight-medium']),
                                    style: {"font-variant-numeric":"tabular-nums"}
                                  }, _toDisplayString(_unref(formatCurrency)(lien.amount)), 3)
                                ])
                              ], 10, _hoisted_4))
                            }), 128)),
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("span", null, _toDisplayString(group.displayName) + " Subtotal", 1),
                              _createElementVNode("span", null, _toDisplayString(_unref(formatCurrency)(group.subtotal)), 1)
                            ])
                          ])), [
                            [_vShow, group.liens.length > 0]
                          ])
                        }), 128)),
                        (!__props.redesignEnabled)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              _cache[8] || (_cache[8] = _createElementVNode("span", null, "TOTAL LIENS", -1)),
                              _createElementVNode("span", null, _toDisplayString(_unref(formatCurrency)(grandTotal.value)), 1)
                            ]))
                          : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                              _cache[9] || (_cache[9] = _createElementVNode("span", { class: "text-body-1 font-weight-bold" }, " Total Liens ", -1)),
                              _createElementVNode("span", {
                                class: _normalizeClass(["font-weight-bold", grandTotal.value > 0 ? 'text-green-darken-2' : 'text-grey-lighten-1']),
                                style: {"font-size":"16px"}
                              }, _toDisplayString(_unref(formatCurrency)(grandTotal.value)), 3)
                            ]))
                      ], 64)),
                  _createElementVNode("div", _hoisted_12, [
                    _createVNode(LlSecondaryButton, {
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('go-back')))
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode(" ← Back ", -1)
                      ]))]),
                      _: 1
                    }),
                    _createVNode(LlTooltip, { text: confirmDisabledReason.value }, {
                      activator: _withCtx(() => [
                        _createVNode(LlSecondaryButton, {
                          color: "primary",
                          disabled: !!liensData.value?.hasUnfinalizedLiens || uiState.loadError,
                          loading: uiState.saving,
                          "data-cy": "confirm-liens-btn",
                          onClick: confirmLiens
                        }, {
                          default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                            _createTextVNode(" Confirm Liens → ", -1)
                          ]))]),
                          _: 1
                        }, 8, ["disabled", "loading"])
                      ]),
                      _: 1
                    }, 8, ["text"])
                  ])
                ], 64))
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(LienFormPanel, {
        modelValue: showEditPanel.value,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showEditPanel).value = $event)),
        "matter-id": __props.matterId,
        lien: editingLien.value,
        loading: panelLoading.value,
        onSaved: onLienSaved
      }, null, 8, ["modelValue", "matter-id", "lien", "loading"])
    ]))
  ], 64))
}
}

})